<script setup lang="ts">
import { computed, ref } from "vue";

import type { FollowingListSectionProps } from "@/components/following/types";

import AsideSectionTitle from "@/dsComponents/modals/subcomponents/AsideSectionTitle.vue";

const props = withDefaults(defineProps<FollowingListSectionProps>(), {
    defaultOpen: true,
    open: undefined,
});

const emit = defineEmits<{
    "update:open": [value: boolean];
}>();

const isControlled = computed(() => props.open !== undefined);
const internalOpen = ref(props.defaultOpen);

const isOpen = computed(() => (isControlled.value ? props.open : internalOpen.value));

const handleToggleSection = (value: boolean): void => {
    if (isControlled.value) {
        emit("update:open", value);
    } else {
        internalOpen.value = value;
    }
};
</script>

<template>
    <section class="following-list-section">
        <div class="following-list-section__header">
            <div class="following-list-section__title">
                <AsideSectionTitle :title="title" size="sm" :default-open="isOpen ?? defaultOpen" @toggle-section="handleToggleSection" />
            </div>
            <div class="following-list-section__divider">
                <div class="following-list-section__divider-line" />
            </div>
        </div>

        <div v-if="isOpen" class="following-list-section__body" role="list">
            <slot />
        </div>
    </section>
</template>

<style scoped lang="scss">
.following-list-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl, 16px);
    width: 100%;

    &__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-xl, 16px);
        width: 100%;
    }

    &__title {
        flex-shrink: 0;
    }

    &__divider {
        flex: 1 0 0;
        min-width: 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
        padding: 1px 0;
    }

    &__divider-line {
        width: 100%;
        height: 1px;
        background-color: var(--stroke-soft-200, #dbdfe6);
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xl, 16px);
        width: 100%;
    }
}
</style>
